import {
	Injectable,
	NgZone,
	OnDestroy,
	signal,
	Signal,
	WritableSignal,
} from '@angular/core';
import { AsyncSubject, interval, Observable, Subscriber } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import 'temporal-polyfill/global';

const MILLISECONDS_PER_SECOND = 1000;
const HEARTBEAT_INTERVAL_MILLISECONDS = MILLISECONDS_PER_SECOND;
const DEFAULT_ALARM_WINDOW_MILLISECONDS = HEARTBEAT_INTERVAL_MILLISECONDS * 2;
const MAX_CADENCE_INTERVAL_SECONDS = 24 * 60 * 60;

interface CurrentTimeOverride {
	baseCurrentInstant: Temporal.Instant;
	baseRealInstant: Temporal.Instant;
	timeZoneId: string;
}

interface CurrentTimeAlarmRegistration {
	id: string;
	alarmInstant: Temporal.Instant;
	inertInstant: Temporal.Instant;
	subscriber: Subscriber<null>;
	triggerId: number | null;
}

@Injectable({
	providedIn: 'root',
})
export class CurrentTimeService implements OnDestroy {
	private _unsubscribe$: AsyncSubject<null> | null = new AsyncSubject<null>();

	private readonly _alarms = new Map<string, CurrentTimeAlarmRegistration>();
	private readonly _cadences = new Map<
		number,
		WritableSignal<Temporal.ZonedDateTime>
	>();
	private readonly _currentTimeSignal = signal(this._getCurrentTime());
	private readonly _currentDaySignal = signal(this._currentTimeSignal());
	private _lastPublishedInstant = this._currentTimeSignal().toInstant();
	private _override: CurrentTimeOverride | null = null;

	private _anonymousAlarmId = 0;
	private _triggerId = 0;

	public readonly currentTime = this._currentTimeSignal.asReadonly();
	/**
	 * A current-time snapshot for the calendar day in the published clock's
	 * timezone.
	 *
	 * This signal changes on the first published heartbeat in a new day and
	 * republishes immediately after explicit clock changes so time-travel
	 * consumers can reconcile without knowing how the clock was changed.
	 */
	public readonly currentDay = this._currentDaySignal.asReadonly();

	constructor(private readonly _ngZone: NgZone) {
		this._ngZone.runOutsideAngular(() => {
			interval(HEARTBEAT_INTERVAL_MILLISECONDS)
				.pipe(takeUntil(this._unsubscribe$))
				.subscribe(() => {
					this._publishCurrentTime(this._getCurrentTime());
				});
		});
	}

	ngOnDestroy(): void {
		if (this._unsubscribe$ == null) {
			return;
		}

		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;

		for (const registration of [...this._alarms.values()]) {
			registration.subscriber.complete();
		}

		this._alarms.clear();
	}

	public now(): Temporal.ZonedDateTime {
		return this._getCurrentTime();
	}

	public setCurrentTime(value: Temporal.ZonedDateTime): void {
		const realInstant = Temporal.Now.instant();

		this._override = {
			baseCurrentInstant: value.toInstant(),
			baseRealInstant: realInstant,
			timeZoneId: value.timeZoneId,
		};
		this._publishCurrentTime(this._getCurrentTime(realInstant), true);
	}

	public reset(): void {
		const realInstant = Temporal.Now.instant();

		this._override = null;
		this._publishCurrentTime(this._getCurrentTime(realInstant), true);
	}

	/** Returns whether an active alarm is registered under the supplied ID. */
	public hasAlarm(id: string): boolean {
		return this._alarms.has(id);
	}

	/**
	 * Returns a memoized clock signal whose normal updates occur after the
	 * requested whole-second cadence, up to 24 hours, has elapsed since its last
	 * publication.
	 *
	 * Explicit clock changes are always published immediately, including when
	 * the changed time remains within the signal's current cadence interval.
	 */
	public currentTimeAt(
		intervalSeconds: number,
	): Signal<Temporal.ZonedDateTime> {
		if (
			!Number.isSafeInteger(intervalSeconds) ||
			intervalSeconds <= 0 ||
			intervalSeconds > MAX_CADENCE_INTERVAL_SECONDS
		) {
			throw new RangeError(
				`The current-time interval must be a positive whole number of seconds no greater than ${MAX_CADENCE_INTERVAL_SECONDS}.`,
			);
		}

		if (intervalSeconds === 1) {
			return this.currentTime;
		}

		const intervalMilliseconds = intervalSeconds * MILLISECONDS_PER_SECOND;
		const existingCadence = this._cadences.get(intervalMilliseconds);
		if (existingCadence != null) {
			return existingCadence.asReadonly();
		}

		const currentTime = this._currentTimeSignal();
		const cadenceSignal = signal(currentTime);
		this._cadences.set(intervalMilliseconds, cadenceSignal);

		return cadenceSignal.asReadonly();
	}

	/**
	 * Creates an alarm that emits while the clock is in `[alarmAt, inertAt)`.
	 *
	 * New registrations are evaluated against the latest published `currentTime`
	 * snapshot. Firing state belongs only to the active registration and is
	 * discarded when it unsubscribes. Explicit time changes and detected
	 * backward movement make active alarms eligible to emit again. The observable
	 * remains subscribed after emitting, and an ID prevents another active
	 * subscription from arming the same alarm.
	 */
	public alarmAt$(
		alarmAt: Temporal.ZonedDateTime,
		inertAt = alarmAt.add({
			milliseconds: DEFAULT_ALARM_WINDOW_MILLISECONDS,
		}),
		id?: string,
	): Observable<void> {
		const alarmInstant = alarmAt.toInstant();
		const inertInstant = inertAt.toInstant();

		if (Temporal.Instant.compare(inertInstant, alarmInstant) <= 0) {
			throw new RangeError(
				'The alarm inert time must be after its alarm time.',
			);
		}

		if (id != null && this.hasAlarm(id)) {
			throw this._createDuplicateAlarmError(id);
		}

		return new Observable<void>((subscriber) => {
			if (this._unsubscribe$ == null) {
				subscriber.complete();
				return undefined;
			}

			const registrationId = id ?? `auto-alarm-id-${this._anonymousAlarmId++}`;

			if (this._alarms.has(registrationId)) {
				subscriber.error(this._createDuplicateAlarmError(registrationId));
				return undefined;
			}

			const currentInstant = this.currentTime().toInstant();

			const registration: CurrentTimeAlarmRegistration = {
				id: registrationId,
				alarmInstant,
				inertInstant,
				subscriber,
				triggerId: null,
			};

			this._alarms.set(registration.id, registration);
			this._checkAlarm(registration, currentInstant);

			return () => {
				this._removeAlarm(registration);
			};
		});
	}

	private _createDuplicateAlarmError(id: string): Error {
		return new Error(`An alarm with ID "${id}" is already armed.`);
	}

	private _checkAlarm(
		registration: CurrentTimeAlarmRegistration,
		currentInstant: Temporal.Instant,
	): void {
		if (
			registration.triggerId === this._triggerId ||
			Temporal.Instant.compare(currentInstant, registration.alarmInstant) < 0 ||
			Temporal.Instant.compare(currentInstant, registration.inertInstant) >= 0
		) {
			return;
		}

		this._emitAlarm(registration);
	}

	private _emitAlarm(registration: CurrentTimeAlarmRegistration): void {
		registration.triggerId = this._triggerId;

		this._ngZone.run(() => {
			registration.subscriber.next(null);
		});
	}

	private _getCurrentTime(
		realInstant = Temporal.Now.instant(),
	): Temporal.ZonedDateTime {
		if (this._override == null) {
			return realInstant.toZonedDateTimeISO(Temporal.Now.timeZoneId());
		}

		const elapsedNanoseconds =
			realInstant.epochNanoseconds -
			this._override.baseRealInstant.epochNanoseconds;

		return Temporal.Instant.fromEpochNanoseconds(
			this._override.baseCurrentInstant.epochNanoseconds + elapsedNanoseconds,
		).toZonedDateTimeISO(this._override.timeZoneId);
	}

	private _checkAllAlarms(currentTime: Temporal.ZonedDateTime): void {
		const currentInstant = currentTime.toInstant();

		for (const registration of this._alarms.values()) {
			this._checkAlarm(registration, currentInstant);
		}
	}

	private _publishCurrentTime(
		currentTime: Temporal.ZonedDateTime,
		explicitTimeChange = false,
	): void {
		const currentInstant = currentTime.toInstant();
		const movedBackward =
			Temporal.Instant.compare(currentInstant, this._lastPublishedInstant) < 0;

		if (explicitTimeChange || movedBackward) {
			this._triggerId++;
		}

		this._lastPublishedInstant = currentInstant;
		this._currentTimeSignal.set(currentTime);

		if (
			explicitTimeChange ||
			!currentTime.toPlainDate().equals(this._currentDaySignal().toPlainDate())
		) {
			this._currentDaySignal.set(currentTime);
		}

		for (const [intervalMilliseconds, cadence] of this._cadences) {
			if (
				explicitTimeChange ||
				currentTime.epochMilliseconds - cadence().epochMilliseconds >=
					intervalMilliseconds
			) {
				cadence.set(currentTime);
			}
		}

		this._checkAllAlarms(currentTime);
	}

	private _removeAlarm(registration: CurrentTimeAlarmRegistration): void {
		this._alarms.delete(registration.id);
	}
}
